/**
 * Location Data Slice
 *
 * Source of truth for location data (tours, markets).
 * Handles loading, fetching, and basic filtering operations.
 */

import type { Location, Stop } from '@/types/location';
import { locationsApiClient } from '@/features/locations/services/locations-api-client';
import { partitionLocations } from '@/features/locations/services/location-transformer';

export interface LocationDataSlice {
  tours: Location[];
  markets: Location[];
  currentTourId: number | null;
  currentMarketId: number | null;
  locationsUrl: string | null;
  isLoading: boolean;
  error: string | null;

  // Computed
  readonly currentTour: Location | null;
  readonly currentMarket: Location | null;
  getAllLocations: () => Location[];

  // Actions
  setTours: (tours: Location[]) => void;
  setMarkets: (markets: Location[]) => void;
  setCurrentTour: (tourId: number | null) => void;
  setCurrentMarket: (marketId: number | null) => void;
  setLocationsUrl: (url: string) => void;
  fetchLocations: () => Promise<void>;
  loadLocations: (data: { tours?: Location[]; markets?: Location[] }) => void;
  filterByDate: (date: string) => Location[];
  filterByType: (type: 'Tour' | 'Market' | 'Nashville') => Location[];
  setLoading: (isLoading: boolean) => void;
  setError: (error: string | null) => void;
  clearError: () => void;
}

export const createLocationDataSlice = (set: any, get: any): LocationDataSlice => ({
  // Initial state
  tours: [],
  markets: [],
  currentTourId: null,
  currentMarketId: null,
  locationsUrl: null,
  isLoading: false,
  error: null,

  // Computed property: Get current tour
  get currentTour() {
    const state = get();
    if (!state) return null;

    const { tours, currentTourId } = state;
    if (!currentTourId || !tours) return null;

    return tours.find((tour: Location) => tour.id === currentTourId) ?? null;
  },

  // Computed property: Get current market
  get currentMarket() {
    const state = get();
    if (!state) return null;

    const { markets, currentMarketId } = state;
    if (!currentMarketId || !markets) return null;

    return markets.find((market: Location) => market.id === currentMarketId) ?? null;
  },

  // Computed function: Get all locations from tours and markets
  getAllLocations: () => {
    const { tours, markets } = get();
    return [...tours, ...markets];
  },

  /**
   * Set tours data and trigger visibility update
   *
   * @param tours - Array of tour objects
   */
  setTours: (tours) => {
    set({ tours });
    // Note: updateVisibleLocations() will be called by the composed store
  },

  /**
   * Set markets data and trigger visibility update
   *
   * @param markets - Array of market objects
   */
  setMarkets: (markets) => {
    set({ markets });
    // Note: updateVisibleLocations() will be called by the composed store
  },

  /**
   * Set current tour ID
   *
   * @param tourId - Tour ID to select (null to clear)
   */
  setCurrentTour: (tourId) => {
    set({ currentTourId: tourId });
  },

  /**
   * Set current market ID
   *
   * @param marketId - Market ID to select (null to clear)
   */
  setCurrentMarket: (marketId) => {
    set({ currentMarketId: marketId });
  },

  /**
   * Set locations URL and automatically fetch data
   *
   * @param url - URL to fetch location data from (S3 JSON endpoint)
   */
  setLocationsUrl: (url) => {
    set({ locationsUrl: url });
    // Automatically fetch when URL is set
    get().fetchLocations();
  },

  /**
   * Fetch location data from the configured URL
   *
   * @remarks
   * Orchestrates the full fetch workflow:
   * 1. Validates URL exists
   * 2. Fetches data via API client
   * 3. Transforms and partitions response
   * 4. Updates store state
   *
   * Data transformation is handled by location-transformer service.
   * HTTP layer is handled by locations-api-client service.
   *
   * @throws Sets error state if fetch or transformation fails
   */
  fetchLocations: async () => {
    const { locationsUrl } = get();

    if (!locationsUrl) {
      set({ error: 'No locations URL configured' });
      return;
    }

    set({ isLoading: true, error: null });

    try {
      const apiResponse = await locationsApiClient.fetchLocations(locationsUrl);
      const { tours, markets } = partitionLocations(apiResponse);

      set({
        tours,
        markets,
        isLoading: false,
      });

      console.log('[LocationStore] Loaded locations:', {
        tours: tours.length,
        markets: markets.length,
        total: tours.length + markets.length,
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : 'Failed to fetch locations';

      set({
        error: errorMessage,
        isLoading: false,
      });

      console.error('[LocationStore] Fetch error:', errorMessage);
    }
  },

  /**
   * Load location data (tours and markets) and trigger visibility update
   *
   * @param data - Object containing tours and/or markets arrays
   */
  loadLocations: ({ tours, markets }) => {
    set((state: LocationDataSlice) => ({
      tours: tours ?? state.tours,
      markets: markets ?? state.markets,
    }));
    // Note: updateVisibleLocations() will be called by the composed store
  },

  /**
   * Filter locations by date
   *
   * @param date - Date string to filter by (e.g., '2024-06-15')
   * @returns Array of locations with stops on that date
   */
  filterByDate: (date) => {
    const allLocations = get().getAllLocations();

    return allLocations.filter((location: Location) =>
      location.stops.some((stop: Stop) => stop.date === date)
    );
  },

  /**
   * Filter locations by type
   *
   * @param type - Location type ('Tour', 'Market', or 'Nashville')
   * @returns Array of locations matching the type
   */
  filterByType: (type) => {
    const allLocations = get().getAllLocations();

    return allLocations.filter((location: Location) => location.type === type);
  },

  /**
   * Set loading state
   *
   * @param isLoading - Loading state
   */
  setLoading: (isLoading) => {
    set({ isLoading });
  },

  /**
   * Set error state
   *
   * @param error - Error message (null to clear)
   */
  setError: (error) => {
    set({ error });
  },

  /**
   * Clear error state
   */
  clearError: () => set({ error: null }),
});
